"use client";

import { c as _c } from "react/compiler-runtime";
import type { QueryFunction } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import axios, { isAxiosError } from "axios";
import { useDebugValue, useEffect, useRef, useState } from "react";
import { useExperimentalSearchContext } from "src/lib/search/ExperimentalContext";
import { customSortFromOverrides, mergeSearchOverrides, overridesToSearchParams, type SearchOverrides } from "src/lib/search/searchOverrides";
import { isFilmwebSearchDocument, type ISearchResult } from "src/lib/types/search";
interface ISearchData {
  totalResults?: number;
  results?: ITypedSearchResult[];
}
interface ITypedSearchResult extends ISearchResult {
  __typename: "ISearchResult";
}
interface ILocationSearchData {
  totalResults?: number;
  results?: string[];
}

/** Exact hits (strategy=ExactFirst) come from a separate search and are already in the right order: keep them first */
function isExactMatch(sr: ISearchResult) {
  return isFilmwebSearchDocument(sr.document) && sr.document.isExactMatch === true;
}
export function customSortExp2(data: ITypedSearchResult[] | undefined): ITypedSearchResult[] | undefined {
  /** Fra Nils på slack
   * Definisjoner:
        p = TMDB popularity
      f = popularitetsfaktor
        Mål:
      f skal ligge mellom 2 og 0,5.
      Alt skal normaliseres rundt p = 20, dvs at når p = 20 skal f = 1
        Maks popularitet vi tar hensyn til skal være 150, norske titler
      må skaleres opp.
        if (norsk) p = p*10
      if (p > 150) p = 150
      if (p >= 20)
      {
          f = ((p - 20) / 130) + 1
      }
      else
      {
          f = 1 - (20 - p) / 40
      }
        Sorteringen av treff kan nå gjøres på (score * f)
   */
  if (!data) {
    return data;
  }
  console.debug("Using customSortExp2");
  const newScore = data.map(sr => {
    if (isFilmwebSearchDocument(sr.document) && sr.document.popularity) {
      let f = 0;
      // norwegian titles are already boosted in the index
      const p = Math.min(sr.document.popularity, 150);
      if (p >= 20) {
        f = (p - 20) / 130 + 1;
      } else {
        f = 1 - (20 - p) / 40;
      }
      return {
        ...sr,
        score: sr.score * f,
        rawScore: sr.score
      };
    }
    return sr;
  });
  return newScore.sort((a, b) => Number(isExactMatch(b)) - Number(isExactMatch(a)) || (isExactMatch(a) ? 0 : b.score - a.score));
}
type AzUseSearchOptions = {
  streamingOnly?: boolean;
  maxNumItems?: number;
  inputDelay?: number;
  active?: boolean;
  quickSearch?: boolean;
  fuzzyWeight?: number;
  customSortNum?: number | string;
  /** Sent to the search function as they are, see searchOverrides.ts */
  overrides?: SearchOverrides;
  /** Sent to the search function on every search, overrides win over them. See QUICKSEARCH_DEFAULTS and FULLSEARCH_DEFAULTS */
  defaults?: SearchOverrides;
};
export function useAzSearch(searchText, opts) {
  const $ = _c(37);
  let t0;
  if ($[0] !== opts) {
    t0 = opts ?? {};
    $[0] = opts;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    streamingOnly: t1,
    maxNumItems,
    inputDelay: t2,
    active: t3,
    quickSearch: t4,
    fuzzyWeight,
    customSortNum,
    overrides,
    defaults
  } = t0;
  const streamingOnly = t1 === undefined ? false : t1;
  const inputDelay = t2 === undefined ? 400 : t2;
  const active = t3 === undefined ? true : t3;
  const quickSearch = t4 === undefined ? false : t4;
  let t5;
  if ($[2] !== defaults || $[3] !== overrides) {
    t5 = mergeSearchOverrides(defaults, overrides);
    $[2] = defaults;
    $[3] = overrides;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const searchOverrides = t5;
  const expSearchContext = useExperimentalSearchContext();
  const _quickSearch = quickSearch || expSearchContext.qs === "1";
  useDebugValue(searchText, _temp);
  useDebugValue(streamingOnly, _temp2);
  useDebugValue(maxNumItems, _temp3);
  useDebugValue(inputDelay, _temp4);
  useDebugValue(active, _temp5);
  useDebugValue(expSearchContext.sp, _temp6);
  useDebugValue(expSearchContext.qt, _temp7);
  useDebugValue(expSearchContext.sm, _temp8);
  useDebugValue(expSearchContext.qst, _temp9);
  useDebugValue(expSearchContext.pqs, _temp10);
  useDebugValue(customSortNum, _temp11);
  const [_searchText, setSearchText] = useState("");
  let t6;
  if ($[5] !== _searchText) {
    t6 = _searchText?.trim();
    $[5] = _searchText;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = fuzzyWeight ?? expSearchContext.qsw.f;
  let t8;
  if ($[7] !== _quickSearch || $[8] !== customSortNum || $[9] !== expSearchContext.pqs || $[10] !== expSearchContext.qst || $[11] !== expSearchContext.qsw.e || $[12] !== expSearchContext.qsw.p || $[13] !== expSearchContext.qt || $[14] !== expSearchContext.sm || $[15] !== expSearchContext.sp || $[16] !== maxNumItems || $[17] !== searchOverrides || $[18] !== streamingOnly || $[19] !== t6 || $[20] !== t7) {
    t8 = ["azsearch", t6, streamingOnly, maxNumItems, _quickSearch, expSearchContext.sp, expSearchContext.qt, expSearchContext.sm, expSearchContext.qst, t7, expSearchContext.qsw.p, expSearchContext.qsw.e, customSortNum, expSearchContext.pqs, searchOverrides];
    $[7] = _quickSearch;
    $[8] = customSortNum;
    $[9] = expSearchContext.pqs;
    $[10] = expSearchContext.qst;
    $[11] = expSearchContext.qsw.e;
    $[12] = expSearchContext.qsw.p;
    $[13] = expSearchContext.qt;
    $[14] = expSearchContext.sm;
    $[15] = expSearchContext.sp;
    $[16] = maxNumItems;
    $[17] = searchOverrides;
    $[18] = streamingOnly;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== _searchText || $[23] !== active) {
    t9 = active && !!_searchText?.trim();
    $[22] = _searchText;
    $[23] = active;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = {
      queryKey: t8,
      enabled: t9,
      queryFn: azQFn,
      refetchOnWindowFocus: false
    };
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const {
    data,
    isFetched,
    isFetching
  } = useQuery(t10);
  const searchTimer = useRef(null);
  let t11;
  let t12;
  if ($[28] !== _searchText || $[29] !== inputDelay || $[30] !== searchText) {
    t11 = () => {
      if (searchText?.trim() && searchText.trim() !== _searchText) {
        if (searchTimer.current) {
          clearTimeout(searchTimer.current);
          searchTimer.current = null;
        }
        if (inputDelay > 0) {
          searchTimer.current = window.setTimeout(() => {
            setSearchText(searchText.trim());
          }, inputDelay);
        } else {
          setSearchText(searchText.trim());
        }
      } else {
        if (!searchText?.trim()) {
          if (searchTimer.current) {
            clearTimeout(searchTimer.current);
            searchTimer.current = null;
          }
          setSearchText("");
        }
      }
      return () => {
        if (searchTimer.current) {
          clearTimeout(searchTimer.current);
          searchTimer.current = null;
        }
      };
    };
    t12 = [searchText, _searchText, inputDelay];
    $[28] = _searchText;
    $[29] = inputDelay;
    $[30] = searchText;
    $[31] = t11;
    $[32] = t12;
  } else {
    t11 = $[31];
    t12 = $[32];
  }
  useEffect(t11, t12);
  let t13;
  if ($[33] !== data || $[34] !== isFetched || $[35] !== isFetching) {
    t13 = {
      searchLoading: isFetching,
      searchLoaded: isFetched,
      searchResult: data
    };
    $[33] = data;
    $[34] = isFetched;
    $[35] = isFetching;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
function _temp11(qsn) {
  return `Custom Sort: ${qsn}`;
}
function _temp10(pqs) {
  return `Pad quick search: ${pqs}`;
}
function _temp9(qst) {
  return `Query string type: ${qst}`;
}
function _temp8(sm) {
  return `Search mode: ${sm}`;
}
function _temp7(qt) {
  return `Query type: ${qt}`;
}
function _temp6(sp) {
  return `Scoring Profile: ${sp}`;
}
function _temp5(active_0) {
  return `active: ${active_0}`;
}
function _temp4(inputDelay_0) {
  return `inputDelay: ${inputDelay_0}`;
}
function _temp3(maxNumItems_0) {
  return `maxNumItems: ${maxNumItems_0}`;
}
function _temp2(streamingOnly_0) {
  return `streamingOnly: ${streamingOnly_0}`;
}
function _temp(searchText_0) {
  return `searchText: ${searchText_0}`;
}
const azQFn: QueryFunction<ISearchData> = async context => {
  const {
    signal
  } = context;
  const searchTerm = context.queryKey[1];
  const streamingOnly = context.queryKey[2] as boolean;
  const maxNumItems = context.queryKey[3] as number | undefined;
  const quickSearch = context.queryKey[4] as boolean;
  const scoringProfile = context.queryKey[5];
  const queryType = context.queryKey[6];
  const searchMode = context.queryKey[7];
  const queryStringType = context.queryKey[8];
  const queryStringWf = context.queryKey[9];
  const queryStringWp = context.queryKey[10];
  const queryStringWe = context.queryKey[11];
  const padQs = context.queryKey[13] as string | undefined;
  const overrides = context.queryKey[14] as SearchOverrides | undefined;
  const customSort = customSortFromOverrides(overrides) ?? context.queryKey[12] as number | string | undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params: any = {
    so: streamingOnly ? "1" : "0",
    sp: scoringProfile,
    qt: queryType,
    sm: searchMode,
    qst: queryStringType,
    q: searchTerm,
    qswf: queryStringWf,
    qswp: queryStringWp,
    qswe: queryStringWe,
    qs: quickSearch ? "1" : "0",
    pqs: padQs
  };
  if (maxNumItems) {
    params.max = maxNumItems;
  }

  // overrides win over the component's own settings
  Object.assign(params, overridesToSearchParams(overrides));
  const axiosResponse = await axios.request<ISearchData>({
    url: process.env.NEXT_PUBLIC_SEARCH_ENDPOINT,
    method: "GET",
    params,
    signal
  });
  if (isAxiosError(axiosResponse)) {
    throw axiosResponse;
  }
  const documentTypename = process.env.NEXT_PUBLIC_WEBSITE_BRAND === "kinodk" ? "IKinodkSearchDocument" as const : "IFilmwebSearchDocument" as const;
  const result = {
    ...axiosResponse.data,
    results: axiosResponse.data.results?.map(d => ({
      ...d,
      __typename: "ISearchResult",
      document: {
        ...d.document,
        __typename: documentTypename
      }
    }) as ITypedSearchResult)
  };
  if (!quickSearch || quickSearch && (result.totalResults ?? 0) > (maxNumItems ?? 0)) switch (customSort) {
    case "2":
    case 2:
      console.debug("Custom sort 2", result);
      result.results = customSortExp2(result.results)!;
      break;
  }
  return result;
};
type UseLocationSearchOptions = {
  inputDelay?: number;
  active?: boolean;
  maxNumItems?: number;
};
export function useAzLocationSearch(searchText, opts) {
  const $ = _c(27);
  let t0;
  if ($[0] !== opts) {
    t0 = opts ?? {};
    $[0] = opts;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    inputDelay: t1,
    active: t2,
    maxNumItems
  } = t0;
  const inputDelay = t1 === undefined ? 400 : t1;
  const active = t2 === undefined ? true : t2;
  useDebugValue(searchText, _temp12);
  useDebugValue(inputDelay, _temp13);
  useDebugValue(maxNumItems, _temp14);
  useDebugValue(active, _temp15);
  const [_searchText, setSearchText] = useState("");
  let t3;
  if ($[2] !== _searchText) {
    t3 = _searchText?.trim();
    $[2] = _searchText;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== maxNumItems || $[5] !== t3) {
    t4 = ["azlocsearch", t3, maxNumItems, 1, 1];
    $[4] = maxNumItems;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== _searchText || $[8] !== active) {
    t5 = active && !!_searchText?.trim();
    $[7] = _searchText;
    $[8] = active;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = {
      queryKey: t4,
      enabled: t5,
      queryFn: azLQFn,
      refetchOnWindowFocus: false
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    data,
    isFetched,
    isFetching
  } = useQuery(t6);
  const searchTimer = useRef(null);
  let t7;
  if ($[13] !== _searchText || $[14] !== inputDelay || $[15] !== searchText) {
    t7 = () => {
      if (searchText?.trim() && searchText.trim() !== _searchText) {
        if (searchTimer.current) {
          clearTimeout(searchTimer.current);
          searchTimer.current = null;
        }
        if (inputDelay > 0) {
          searchTimer.current = window.setTimeout(() => {
            setSearchText(searchText.trim());
          }, inputDelay);
        } else {
          setSearchText(searchText.trim());
        }
      } else {
        if (!searchText?.trim()) {
          if (searchTimer.current) {
            clearTimeout(searchTimer.current);
            searchTimer.current = null;
          }
          setSearchText("");
        }
      }
      return () => {
        if (searchTimer.current) {
          clearTimeout(searchTimer.current);
          searchTimer.current = null;
        }
      };
    };
    $[13] = _searchText;
    $[14] = inputDelay;
    $[15] = searchText;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== _searchText || $[18] !== inputDelay || $[19] !== isFetching || $[20] !== searchText) {
    t8 = [searchText, _searchText, inputDelay, isFetching];
    $[17] = _searchText;
    $[18] = inputDelay;
    $[19] = isFetching;
    $[20] = searchText;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useEffect(t7, t8);
  let t9;
  if ($[22] !== _searchText || $[23] !== data || $[24] !== isFetched || $[25] !== isFetching) {
    t9 = {
      searchLoading: isFetching,
      searchLoaded: isFetched,
      searchResult: data,
      currentSearchText: _searchText
    };
    $[22] = _searchText;
    $[23] = data;
    $[24] = isFetched;
    $[25] = isFetching;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
function _temp15(active_0) {
  return `active: ${active_0}`;
}
function _temp14(maxNumItems_0) {
  return `maxNumItems: ${maxNumItems_0}`;
}
function _temp13(inputDelay_0) {
  return `inputDelay: ${inputDelay_0}`;
}
function _temp12(searchText_0) {
  return `searchText: ${searchText_0}`;
}
const azLQFn: QueryFunction<ILocationSearchData> = async context => {
  const {
    signal
  } = context;
  const searchTerm = context.queryKey[1];
  const maxNumItems = context.queryKey[2] as number | undefined;
  const queryStringWp = context.queryKey[3];
  const queryStringWe = context.queryKey[4];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params: any = {
    q: searchTerm,
    qswp: queryStringWp,
    qswe: queryStringWe
  };
  if (maxNumItems) {
    params.max = maxNumItems;
  }
  const axiosResponse = await axios.request<ILocationSearchData>({
    url: process.env.NEXT_PUBLIC_LOCSEARCH_ENDPOINT,
    method: "GET",
    params,
    signal
  });
  if (isAxiosError(axiosResponse)) {
    throw axiosResponse;
  }
  return axiosResponse.data;
};